import { Copy, X as Cancel, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import type { Run } from "@/api/runs";
import { useCancelRun, useRetryRun } from "@/api/submission";
import { RunStateBadge } from "@/components/runs/run-state-badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

const TERMINAL_FAILURE = new Set([
  "failed_validation",
  "failed_system",
  "failed_contract",
  "failed_scientific",
]);

const ACTIVE = new Set([
  "validating",
  "cached",
  "staging_inputs",
  "dispatched",
  "running",
  "collecting_outputs",
  "validating_outputs",
]);

/**
 * Run-detail page header — title, state badge, breadcrumbs, action
 * rail (Cancel / Retry / copy run_id).
 *
 * The cancel + retry mutations live behind useCancelRun/useRetryRun
 * (B2). Buttons gate on the run's current state per the lifecycle:
 *   - Cancel: visible only on active states.
 *   - Retry: visible only on terminal-failure states.
 */
export function RunHeader({ run }: { run: Run }): JSX.Element {
  const workload = run.flow_name ?? run.tile_name ?? run.run_id;
  const cancel = useCancelRun();
  const retry = useRetryRun();
  const isActive = ACTIVE.has(run.status);
  const isFailed = TERMINAL_FAILURE.has(run.status);

  const copyRunId = (): void => {
    navigator.clipboard
      .writeText(run.run_id)
      .then(() => toast.success("Run ID copied"))
      .catch(() => toast.error("Failed to copy run ID"));
  };

  return (
    <div className="flex flex-col gap-3 border-b border-border-subtle pb-4">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        {run.project_slug && (
          <>
            <Link to={`/projects/${run.project_slug}`} className="hover:text-foreground">
              {run.project_slug}
            </Link>
            <span aria-hidden>·</span>
          </>
        )}
        <Link to="/runs" className="hover:text-foreground">
          Runs
        </Link>
        <span aria-hidden>·</span>
        <span className="font-mono">{run.run_id}</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              aria-label="Copy run ID"
              onClick={copyRunId}
            >
              <Copy className="h-3 w-3" aria-hidden />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Copy run ID</TooltipContent>
        </Tooltip>
      </div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h1 className="font-display text-3xl font-semibold tracking-tight text-foreground">
            {workload}
          </h1>
          <div className="flex items-center gap-3">
            <RunStateBadge state={run.status} size="md" />
            <span className="font-mono text-xs text-muted-foreground">{run.run_id}</span>
            <span className="text-xs text-muted-foreground">
              submitted{" "}
              <time dateTime={run.submitted_at} title={new Date(run.submitted_at).toLocaleString()}>
                {new Date(run.submitted_at).toLocaleString()}
              </time>{" "}
              by {run.submitted_by}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isActive && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                cancel.mutate(run.run_id, {
                  onSuccess: (data) => toast.success(`Run cancelled (${data.state})`),
                  onError: (err) => toast.error(`Cancel failed: ${(err as Error).message}`),
                });
              }}
              disabled={cancel.isPending}
            >
              <Cancel className="h-3.5 w-3.5" aria-hidden />
              Cancel
            </Button>
          )}
          {isFailed && (
            <Button
              variant="default"
              size="sm"
              onClick={() => {
                retry.mutate(
                  { runId: run.run_id, projectSlug: run.project_slug },
                  {
                    onSuccess: (data) => toast.success(`Retry queued (${data.run_id})`),
                    onError: (err) => toast.error(`Retry failed: ${(err as Error).message}`),
                  }
                );
              }}
              disabled={retry.isPending}
            >
              <RefreshCw className="h-3.5 w-3.5" aria-hidden />
              Retry
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
